import { useCallback, useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SITE_LABEL } from "@/components/Layout";
import { useConfirm } from "@/components/useConfirm";
import { toast } from "sonner";
import { ChefHat, Plus, Pencil, Trash2, Download, Clock, Users, AlertTriangle, Wifi, Search } from "lucide-react";

const PHOTO_URL = (id) => `${API_BASE}/recipes/${id}/photo`;

function CostingSection({ form, setForm }) {
  const lines = form.cost_lines;
  const linesTotal = lines.reduce((s, l) => s + (parseFloat(l.cost_nzd) || 0), 0);
  const override = form.cost_total_override !== "" ? parseFloat(form.cost_total_override) : null;
  const total = Number.isFinite(override) ? override : linesTotal;
  const portions = parseFloat(form.cost_portions) || 0;
  const perPortion = portions > 0 && total > 0 ? total / portions : null;

  const setLine = (i, key, val) => {
    const next = [...lines];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, cost_lines: next });
  };

  return (
    <div className="rounded-lg border border-[#C9A84C]/40 bg-[#FBF7EC] p-3 space-y-3" data-testid="recipe-costing">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-[#1A2744]">Food costing</Label>
        <span className="text-[11px] text-[#9a7d2e]">Only admins and recipe editors see this</span>
      </div>

      <div className="space-y-2">
        {lines.map((ln, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <Input
              className="col-span-12 sm:col-span-5 h-9 bg-white"
              placeholder="Ingredient"
              value={ln.item}
              onChange={(e) => setLine(i, "item", e.target.value)}
              data-testid={`cost-item-${i}`}
            />
            <Input
              className="col-span-3 sm:col-span-2 h-9 bg-white"
              placeholder="Qty"
              value={ln.qty}
              onChange={(e) => setLine(i, "qty", e.target.value)}
              data-testid={`cost-qty-${i}`}
            />
            <Input
              className="col-span-3 sm:col-span-2 h-9 bg-white"
              placeholder="Unit"
              value={ln.unit}
              onChange={(e) => setLine(i, "unit", e.target.value)}
              data-testid={`cost-unit-${i}`}
            />
            <div className="col-span-4 sm:col-span-2 relative">
              <span className="absolute left-2 top-2 text-xs text-gray-400">$</span>
              <Input
                className="h-9 pl-5 bg-white text-right"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={ln.cost_nzd}
                onChange={(e) => setLine(i, "cost_nzd", e.target.value)}
                data-testid={`cost-amount-${i}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="col-span-2 sm:col-span-1 h-9 px-0 text-red-600"
              onClick={() => setForm({ ...form, cost_lines: lines.filter((_, x) => x !== i) })}
              data-testid={`cost-remove-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setForm({ ...form, cost_lines: [...lines, { item: "", qty: "", unit: "", cost_nzd: "" }] })}
          data-testid="cost-add-line"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add costed ingredient
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-600">Override total ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            className="bg-white"
            placeholder={linesTotal ? linesTotal.toFixed(2) : "auto from lines"}
            value={form.cost_total_override}
            onChange={(e) => setForm({ ...form, cost_total_override: e.target.value })}
            data-testid="cost-total-override"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">Portions this makes</Label>
          <Input
            type="number"
            min="0"
            step="1"
            className="bg-white"
            placeholder="e.g. 12"
            value={form.cost_portions}
            onChange={(e) => setForm({ ...form, cost_portions: e.target.value })}
            data-testid="cost-portions"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 border-t border-[#C9A84C]/40 pt-2 text-sm" data-testid="cost-summary">
        <span className="text-gray-600">Lines: <strong>${linesTotal.toFixed(2)}</strong></span>
        <span className="text-[#1A2744]">Total food cost: <strong>${total.toFixed(2)}</strong></span>
        <span className="text-[#1A2744]">
          Per portion: <strong>{perPortion != null ? `$${perPortion.toFixed(2)}` : "—"}</strong>
        </span>
      </div>
    </div>
  );
}

function RecipeFormDialog({ recipe, categories, sites, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blank = {
    title: "", category: categories[0] || "Other", sites: sites.map((s) => s.code),
    ingredients: "", method: "", yield_portions: "", prep_time_minutes: "",
    allergens: "", staff_notes: "",
    cost_lines: [], cost_total_override: "", cost_portions: "",
  };
  const [form, setForm] = useState(blank);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPhoto(null);
    setForm(recipe ? {
      title: recipe.title || "",
      category: recipe.category || categories[0] || "Other",
      sites: recipe.sites || [],
      ingredients: recipe.ingredients || "",
      method: recipe.method || "",
      yield_portions: recipe.yield_portions || "",
      prep_time_minutes: recipe.prep_time_minutes ?? "",
      allergens: recipe.allergens || "",
      staff_notes: recipe.staff_notes || "",
      cost_lines: (recipe.cost_lines || []).map((l) => ({
        item: l.item || "", qty: l.qty || "", unit: l.unit || "",
        cost_nzd: l.cost_nzd != null ? String(l.cost_nzd) : "",
      })),
      cost_total_override: recipe.cost_total_override != null ? String(recipe.cost_total_override) : "",
      cost_portions: recipe.cost_portions != null ? String(recipe.cost_portions) : "",
    } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipe]);

  const toggleSite = (code) =>
    setForm((f) => ({
      ...f,
      sites: f.sites.includes(code) ? f.sites.filter((s) => s !== code) : [...f.sites, code],
    }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Give the recipe a title"); return; }
    if (form.sites.length === 0) { toast.error("Pick at least one location"); return; }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("sites", form.sites.join(","));
    fd.append("ingredients", form.ingredients);
    fd.append("method", form.method);
    fd.append("yield_portions", form.yield_portions);
    if (form.prep_time_minutes !== "") fd.append("prep_time_minutes", String(parseInt(form.prep_time_minutes, 10)));
    fd.append("allergens", form.allergens);
    fd.append("staff_notes", form.staff_notes);
    fd.append("cost_lines", JSON.stringify(
      form.cost_lines
        .filter((l) => (l.item || "").trim() !== "" || parseFloat(l.cost_nzd) > 0)
        .map((l) => ({ ...l, cost_nzd: parseFloat(l.cost_nzd) || 0 })),
    ));
    if (form.cost_total_override !== "") fd.append("cost_total_override", String(parseFloat(form.cost_total_override)));
    else fd.append("clear_cost_override", "true");
    if (form.cost_portions !== "") fd.append("cost_portions", String(parseFloat(form.cost_portions)));
    if (photo) fd.append("photo", photo);
    setBusy(true);
    try {
      if (recipe) await api.patch(`/recipes/${recipe.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/recipes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(recipe ? "Recipe updated" : "Recipe added");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the recipe");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{recipe ? "Edit recipe" : "Add a recipe"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. House Chai Latte"
                data-testid="recipe-form-title"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="recipe-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Locations</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {sites.map((s) => (
                <Button
                  key={s.code}
                  type="button"
                  size="sm"
                  variant={form.sites.includes(s.code) ? "default" : "outline"}
                  className={form.sites.includes(s.code) ? "bg-[#1A2744] text-white" : ""}
                  onClick={() => toggleSite(s.code)}
                  data-testid={`recipe-form-site-${s.code}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-gray-500">Pick one location, or several when the recipe is shared.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Yield / portion</Label>
              <Input
                value={form.yield_portions}
                onChange={(e) => setForm({ ...form, yield_portions: e.target.value })}
                placeholder="e.g. 12 serves"
                data-testid="recipe-form-yield"
              />
            </div>
            <div>
              <Label>Prep time (min)</Label>
              <Input
                type="number"
                min="0"
                value={form.prep_time_minutes}
                onChange={(e) => setForm({ ...form, prep_time_minutes: e.target.value })}
                data-testid="recipe-form-prep"
              />
            </div>
            <div>
              <Label>Allergens</Label>
              <Input
                value={form.allergens}
                onChange={(e) => setForm({ ...form, allergens: e.target.value })}
                placeholder="e.g. Dairy, nuts"
                data-testid="recipe-form-allergens"
              />
            </div>
          </div>

          <div>
            <Label>Ingredients <span className="text-gray-400 text-xs">(one per line)</span></Label>
            <Textarea
              rows={5}
              value={form.ingredients}
              onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
              placeholder={"250ml oat milk\n2 shots espresso\n15ml vanilla syrup"}
              data-testid="recipe-form-ingredients"
            />
          </div>
          <div>
            <Label>Method <span className="text-gray-400 text-xs">(one step per line)</span></Label>
            <Textarea
              rows={6}
              value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value })}
              placeholder={"1. Steam the milk to 62°C\n2. Pull a double shot"}
              data-testid="recipe-form-method"
            />
          </div>
          <div>
            <Label>Staff notes <span className="text-gray-400 text-xs">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.staff_notes}
              onChange={(e) => setForm({ ...form, staff_notes: e.target.value })}
              placeholder="Upsell with the almond croissant"
              data-testid="recipe-form-notes"
            />
          </div>
          <div>
            <Label>Photo {recipe?.has_photo && <span className="text-gray-400 text-xs">(replaces current)</span>}</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
              data-testid="recipe-form-photo"
            />
          </div>
          <CostingSection form={form} setForm={setForm} />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid="recipe-form-save">
            {busy ? "Saving…" : recipe ? "Save changes" : "Add recipe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecipeDetailDialog({ recipe, open, onOpenChange }) {
  if (!recipe) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-detail">
        <DialogHeader><DialogTitle>{recipe.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="border-[#C9A84C] text-[#9a7d2e]">{recipe.category}</Badge>
            {(recipe.site_labels || []).map((l) => (
              <Badge key={l} variant="outline" className="text-gray-600">{l}</Badge>
            ))}
          </div>
          {recipe.has_photo && (
            <img
              src={PHOTO_URL(recipe.id)}
              alt={recipe.title}
              className="w-full max-h-72 object-cover rounded-lg border border-gray-200"
              data-testid="recipe-detail-photo"
            />
          )}
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            {recipe.yield_portions && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {recipe.yield_portions}</span>}
            {recipe.prep_time_minutes != null && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {recipe.prep_time_minutes} min</span>}
            {recipe.allergens && <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {recipe.allergens}</span>}
          </div>
          {recipe.ingredients && (
            <section>
              <h3 className="font-heading font-semibold text-[#1A2744] mb-1">Ingredients</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                {recipe.ingredients.split("\n").filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </section>
          )}
          {recipe.method && (
            <section>
              <h3 className="font-heading font-semibold text-[#1A2744] mb-1">Method</h3>
              <div className="text-sm text-gray-700 space-y-1">
                {recipe.method.split("\n").filter(Boolean).map((l, i) => <p key={i}>{l}</p>)}
              </div>
            </section>
          )}
          {recipe.staff_notes && (
            <p className="text-sm text-[#1A2744] bg-[#FBF7EC] border border-[#C9A84C]/50 rounded-md p-3">
              <span className="font-semibold">Staff notes: </span>{recipe.staff_notes}
            </p>
          )}
          {recipe.food_cost_total != null && (
            <section className="rounded-md border border-[#C9A84C]/50 bg-[#FBF7EC] p-3" data-testid="recipe-detail-costing">
              <h3 className="font-heading font-semibold text-[#1A2744] mb-1">Food costing</h3>
              <div className="flex flex-wrap gap-4 text-sm text-[#1A2744]">
                <span>Total: <strong>${Number(recipe.food_cost_total).toFixed(2)}</strong></span>
                {recipe.cost_per_portion != null && (
                  <span>Per portion: <strong>${Number(recipe.cost_per_portion).toFixed(2)}</strong></span>
                )}
                {recipe.cost_portions ? <span className="text-gray-500">Makes {recipe.cost_portions}</span> : null}
              </div>
              {(recipe.cost_lines || []).length > 0 && (
                <ul className="mt-2 text-xs text-gray-700 space-y-0.5">
                  {recipe.cost_lines.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span>{l.item}{l.qty ? ` — ${l.qty} ${l.unit}` : ""}</span>
                      <span className="tabular-nums">${Number(l.cost_nzd).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {recipe.costed_at && (
                <p className="mt-2 text-[11px] text-gray-500">
                  Costed {new Date(recipe.costed_at).toLocaleDateString()}
                  {recipe.costed_by_name ? ` by ${recipe.costed_by_name}` : ""}
                </p>
              )}
            </section>
          )}
          <p className="text-[11px] text-gray-400">
            Added by {recipe.created_by_name}
            {recipe.updated_by_name ? ` · last edited by ${recipe.updated_by_name}` : ""}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookDownloadDialog({ sites }) {
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState("ALL");
  const [groupBy, setGroupBy] = useState("category");
  const [withCosts, setWithCosts] = useState(false);
  const href = `${API_BASE}/admin/recipes/book.pdf?site=${site}&group_by=${groupBy}&include_costs=${withCosts}`;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="recipe-book-open">
          <Download className="h-4 w-4 mr-1" /> Download recipe book
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Download recipe book (PDF)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Locations to include</Label>
            <Select value={site} onValueChange={setSite}>
              <SelectTrigger data-testid="recipe-book-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All locations (one book)</SelectItem>
                {sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.label} only</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Group recipes by</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="recipe-book-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="category">Category (location noted on each)</SelectItem>
                <SelectItem value="location">Location, then category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 rounded-md border border-gray-200 p-3 cursor-pointer">
            <Checkbox
              checked={withCosts}
              onCheckedChange={(v) => setWithCosts(!!v)}
              data-testid="recipe-book-costs"
            />
            <span className="text-sm">
              Include costings
              <span className="block text-[11px] text-gray-500">
                Adds total food cost and cost per portion to each recipe. Leave off for the copy that lives on the floor.
              </span>
            </span>
          </label>
          <p className="text-[11px] text-gray-500">
            The PDF opens with a table of contents and gives every recipe its own page with photo.
          </p>
        </div>
        <DialogFooter>
          <a href={href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
            <Button className="bg-[#1A2744] text-white" data-testid="recipe-book-download">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Recipes() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("ALL");
  const [category, setCategory] = useState("ALL");
  const [search, setSearch] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/recipes", { params: { site, category, q: search || undefined } });
      setRecipes(data.recipes);
      setSites(data.sites);
      setCanEdit(data.can_edit);
      setCanDownload(data.can_download_book);
      setBlocked(null);
    } catch (e) {
      if (e?.response?.status === 403) setBlocked(e.response.data.detail);
      else toast.error("Couldn't load recipes");
    }
  }, [site, category, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/recipes/categories").then(({ data }) => setCategories(data.categories)).catch(() => {});
  }, []);

  const remove = async (r) => {
    const ok = await confirm({
      title: `Delete “${r.title}”?`,
      description: "The recipe and its photo are removed for everyone. This can't be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/recipes/${r.id}`);
      toast.success("Recipe deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isAdmin = ["ADMIN", "SUPERADMIN"].includes(user?.role);

  if (blocked) {
    return (
      <div className="max-w-xl mx-auto mt-10 text-center space-y-3" data-testid="recipes-wifi-blocked">
        <Wifi className="h-10 w-10 text-[#C9A84C] mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-[#1A2744]">Cafe Wi-Fi required</h1>
        <p className="text-gray-600">{blocked}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] flex items-center gap-3">
            <ChefHat className="h-8 w-8 text-[#C9A84C]" /> Recipes
          </h1>
          <p className="text-gray-500 mt-1">
            {canEdit
              ? "Add and edit the cafe's recipes — photos, ingredients, method and allergens."
              : "Our house recipes, viewable on the cafe Wi-Fi."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canDownload && <BookDownloadDialog sites={sites} />}
          {canEdit && (
            <RecipeFormDialog
              categories={categories}
              sites={sites}
              onDone={load}
              trigger={
                <Button className="bg-[#1A2744] text-white hover:bg-[#1A2744]/90" data-testid="recipe-add-open">
                  <Plus className="h-4 w-4 mr-1" /> Add recipe
                </Button>
              }
            />
          )}
        </div>
      </header>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <Tabs value={site} onValueChange={setSite}>
          <TabsList data-testid="recipe-site-tabs">
            <TabsTrigger value="ALL" data-testid="recipe-site-ALL">All locations</TabsTrigger>
            {sites.map((s) => (
              <TabsTrigger key={s.code} value={s.code} data-testid={`recipe-site-${s.code}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 lg:ml-auto">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px]" data-testid="recipe-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
            <Input
              className="pl-8 w-[200px]"
              placeholder="Search recipes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="recipe-search"
            />
          </div>
        </div>
      </div>

      {recipes.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500" data-testid="recipes-empty">
          No recipes here yet{canEdit ? " — add the first one." : "."}
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="recipes-grid">
          {recipes.map((r) => (
            <Card
              key={r.id}
              className="overflow-hidden border-gray-200 hover:border-[#C9A84C] transition-colors cursor-pointer"
              onClick={() => setDetail(r)}
              data-testid={`recipe-card-${r.id}`}
            >
              {r.has_photo ? (
                <img src={PHOTO_URL(r.id)} alt={r.title} className="h-40 w-full object-cover" />
              ) : (
                <div className="h-28 w-full bg-gradient-to-br from-[#FBF7EC] to-[#F0E6CE] flex flex-col items-center justify-center gap-1 text-[#9a7d2e]">
                  <ChefHat className="h-6 w-6" />
                  <span className="text-[11px] tracking-wide">No photo yet</span>
                </div>
              )}
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-heading font-semibold text-base md:text-lg text-[#1A2744] leading-snug">{r.title}</h2>
                  <Badge variant="outline" className="border-[#C9A84C] text-[#9a7d2e] shrink-0">{r.category}</Badge>
                </div>
                <div className="flex flex-wrap gap-1 text-[11px] text-gray-500">
                  {(r.site_labels || []).map((l) => <span key={l} className="px-1.5 py-0.5 bg-gray-100 rounded">{l}</span>)}
                  {r.prep_time_minutes != null && <span className="px-1.5 py-0.5 bg-gray-100 rounded">{r.prep_time_minutes} min</span>}
                  {r.allergens && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">{r.allergens}</span>}
                  {r.cost_per_portion != null && (
                    <span className="px-1.5 py-0.5 bg-[#FBF7EC] text-[#9a7d2e] border border-[#C9A84C]/50 rounded" data-testid={`recipe-cost-badge-${r.id}`}>
                      ${Number(r.cost_per_portion).toFixed(2)}/portion
                    </span>
                  )}
                  {r.cost_per_portion == null && r.food_cost_total != null && (
                    <span className="px-1.5 py-0.5 bg-[#FBF7EC] text-[#9a7d2e] border border-[#C9A84C]/50 rounded" data-testid={`recipe-cost-badge-${r.id}`}>
                      ${Number(r.food_cost_total).toFixed(2)} cost
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-1 pt-1" onClick={(e) => e.stopPropagation()}>
                    <RecipeFormDialog
                      recipe={r}
                      categories={categories}
                      sites={sites}
                      onDone={load}
                      trigger={
                        <Button size="sm" variant="outline" data-testid={`recipe-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      }
                    />
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(r)} data-testid={`recipe-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RecipeDetailDialog recipe={detail} open={!!detail} onOpenChange={(v) => !v && setDetail(null)} />
    </div>
  );
}
